import React, { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { useAuthStore } from '../../stores/authStore'
import { paymentService } from '../../services/paymentService-v2'
import { useErrorHandler, BaseError, withRetry } from '../../utils/errors'
import { 
  CreditCard, 
  Shield, 
  AlertCircle, 
  CheckCircle, 
  Loader,
  Info 
} from 'lucide-react'

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!)

interface PaymentFormProps {
  amount: number
  serviceType: string // Changed from planType
  serviceTitle: string
  onSuccess: (paymentIntentId: string) => void
  onError: (error: string) => void
  onCancel: () => void
}

const PaymentFormContent: React.FC<PaymentFormProps> = ({
  amount,
  serviceType,
  serviceTitle,
  onSuccess,
  onError,
  onCancel
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const { user } = useAuthStore()
  const { handleError, handleErrorWithCallback, isRetryable, getRetryDelay } = useErrorHandler()

  // State management
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentIntent, setPaymentIntent] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [cardError, setCardError] = useState<string | null>(null)

  // Initialize payment intent
  useEffect(() => {
    initializePaymentIntent()
  }, [])

  const initializePaymentIntent = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      if (!user?.id) {
        throw new Error('User authentication required')
      }

      console.log(`🔄 Creating payment intent for ${serviceType}:`, {
        amount,
        serviceType,
        userId: user.id
      })

      // Create payment intent with retry logic
      const intent = await withRetry(
        () => paymentService.createPaymentIntent({
          amount,
          serviceType,
          userId: user.id.toString(),
        }),
        3,
        'Failed to initialize payment after multiple attempts'
      )

      if (!intent.success || !intent.clientSecret) {
        throw new Error('Invalid payment intent response')
      }

      setPaymentIntent(intent)
      console.log('✅ Payment intent created successfully:', intent.paymentIntentId)

    } catch (error) {
      console.error('❌ Failed to initialize payment intent:', error)
      
      handleErrorWithCallback(error, (err) => {
        setError(err.userMessage)
        
        // If this is a retryable error, offer retry option
        if (isRetryable(err) && retryAttempt < 2) {
          setTimeout(() => {
            setRetryAttempt(prev => prev + 1)
            initializePaymentIntent()
          }, getRetryDelay(err, retryAttempt))
        } else {
          onError(err.userMessage)
        }
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !paymentIntent) {
      setError('Payment system is not ready. Please try again.')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setError('Payment form is not loaded. Please refresh and try again.')
      return
    }

    try {
      setIsProcessing(true)
      setError(null)
      setCardError(null)

      console.log('💳 Processing payment...', {
        paymentIntentId: paymentIntent.paymentIntentId,
        amount: paymentIntent.amount
      })

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: user?.name || user?.email || 'Customer',
              email: user?.email || undefined,
            },
          },
        }
      )

      if (stripeError) {
        console.error('💥 Stripe payment error:', stripeError)
        
        // Handle specific Stripe errors
        const errorMessage = getStripeErrorMessage(stripeError)
        setCardError(errorMessage)
        
        // Also call the parent error handler
        onError(errorMessage)
        return
      }

      if (confirmedIntent && confirmedIntent.status === 'succeeded') {
        console.log('🎉 Payment succeeded:', confirmedIntent.id)
        onSuccess(confirmedIntent.id)
      } else {
        console.warn('⚠️ Payment not completed:', confirmedIntent?.status)
        setError('Payment was not completed. Please try again.')
      }

    } catch (error) {
      console.error('❌ Payment processing error:', error)
      
      handleErrorWithCallback(error, (err) => {
        setError(err.userMessage)
        onError(err.userMessage)
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCardChange = (event: any) => {
    if (event.error) {
      setCardError(event.error.message)
    } else {
      setCardError(null)
    }
  }

  const getStripeErrorMessage = (error: any): string => {
    const errorMessages: Record<string, string> = {
      'card_declined': 'Your card was declined. Please try a different payment method.',
      'insufficient_funds': 'Your card has insufficient funds. Please try a different payment method.',
      'incorrect_cvc': 'Your card\'s security code is incorrect. Please check and try again.',
      'expired_card': 'Your card has expired. Please try a different payment method.',
      'processing_error': 'An error occurred while processing your card. Please try again.',
      'incorrect_number': 'Your card number is incorrect. Please check and try again.',
    }

    return errorMessages[error.code] || error.message || 'Payment failed. Please try again.'
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-8">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <h3 className="text-lg font-semibold mb-2">Initializing Payment</h3>
            <p className="text-gray-600 text-sm">
              Setting up secure payment processing...
            </p>
            {retryAttempt > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                Retry attempt {retryAttempt}/2
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error && !paymentIntent) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-red-900">
              Payment Setup Failed
            </h3>
            <p className="text-red-700 text-sm mb-4">
              {error}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={onCancel} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={initializePaymentIntent} 
                className="flex-1"
                disabled={retryAttempt >= 2}
              >
                {retryAttempt >= 2 ? 'Max Retries' : 'Try Again'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md mx-auto shadow-2xl">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="w-5 h-5 text-blue-600" />
          Complete Your Payment
        </CardTitle>
        <div className="text-sm text-gray-600">
          Secure payment for {serviceTitle}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Service Summary */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-900">Service Deposit</span>
            <span className="text-xl font-bold text-blue-600">${amount}</span>
          </div>
          <div className="text-sm text-gray-600">
            {serviceTitle}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            This deposit will be applied to your service invoice
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="border border-gray-300 rounded-md p-3 bg-white">
              <CardElement
                onChange={handleCardChange}
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#374151',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                    },
                    invalid: {
                      color: '#EF4444',
                    },
                  },
                }}
              />
            </div>
            {cardError && (
              <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {cardError}
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-600">
                Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
              </div>
            </div>
          </div>

          {/* General Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!stripe || isProcessing || !!cardError}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  Processing...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Pay ${amount}
                </div>
              )}
            </Button>
          </div>
        </form>

        {/* Additional Info */}
        <div className="border-t pt-4">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div>
              By completing this payment, you agree to our Terms of Service and Privacy Policy. 
              Your service will be activated within 24 hours of successful payment.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const StripePaymentForm: React.FC<PaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  )
}

export default StripePaymentForm